import {appointmentInstant} from "../services/schedule/appointmentClock";

// Newest visit first, by its start.
const byStartDescending = (a, b) =>
  (appointmentInstant(b) ?? 0) - (appointmentInstant(a) ?? 0);

export function patientReducer(state, action) {
  switch (action.type) {
    case "UPDATE_STATE": {
      return init(action.payload);
    }

    case "UPDATE_PATIENT": {
      let newP = {...state.patient};
      for (let [key, value] of Object.entries(action.payload))
        newP[key] = value;

      if (action.options?.newDoctor) {
        let dids = newP.did;
        dids = [...new Set([...[].concat(dids), action.options?.newDoctor])];
        newP.did = dids;
      }
      return {...state, patient: newP};
    }

    case "NEW_APPOINTMENT": {
      let newP = state.patient;
      if (action.updatePatient) {
        newP = {...newP};
        for (let [key, value] of Object.entries(action.updatePatient))
          newP[key] = value;
      }
      // Newest first, by each visit's start (§2.6 — never `ISOdate`, which a
      // recurring instance keeps from its series' first week).
      let newAppointments = state.appointments
        .concat(action.payload)
        .sort(byStartDescending);

      return {
        ...state,
        appointments: newAppointments,
        patient: newP,
      };
    }

    case "UPDATE_APPOINTMENT": {
      let appointments = state.appointments;
      let apptIndex = appointments.findIndex(
        (a) => a.aid === action.payload.aid
      );
      let patient = state.patient;

      if (action.changingDoctor) {
        let did = [
          ...new Set([].concat(patient.did).concat(action.payload.did)),
        ];
        patient = {...patient, did};
      }

      return {
        ...state,
        appointments:
          apptIndex !== -1
            ? [
                ...appointments.slice(0, apptIndex),
                action.payload,
                ...appointments.slice(apptIndex + 1),
              ]
            : appointments.concat(action.payload),
        patient,
        selectedAppointment:
          state.selectedAppointment?.aid === action.payload.aid
            ? action.payload
            : state.selectedAppointment,
      };
    }

    case "SELECT_APPOINTMENT": {
      return {...state, selectedAppointment: action.payload};
    }

    case "NEW_NOTIFICATION": {
      return {
        ...state,
        notifications: [action.payload, ...state.notifications],
      };
    }

    case "READ_NOTIFICATIONS": {
      return {
        ...state,
        notifications: state.notifications.map((e) => ({...e, read: true})),
      };
    }
    case "CLEAR_NOTIFICATIONS": {
      return {...state, notifications: []};
    }
    case "DELETE_NOTIFICATION": {
      return {
        ...state,
        notifications: state.notifications.filter(
          (e) => e.ntfId !== action.payload.ntfId
        ),
      };
    }
    case "SET_APPOINTMENTS": {
      return {
        ...state,
        appointments: action.payload.slice().sort(byStartDescending),
      };
    }
    case "CHANGE_LOCATION": {
      return {...state, selectedLocation: action.payload};
    }
    case "SET_BOOKNOW": {
      return {...state, bookNow: action.payload};
    }
    case "CHANGING_LOCATION_LOADER": {
      return {...state, changingLocation: action.payload};
    }

    case "UPDATE_GUARDIAN_LINKS": {
      return {...state, guardianOf: action.payload || []};
    }

    // What the SERVER said this guardian may reach for this dependent:
    // `sections`, `capabilities` and `recordScope`, computed by the policy.
    //
    // This used to hold `accessConfig` - two booleans the browser then used to
    // decide which nav items to draw. Two problems at once: the client was
    // deciding, and it was deciding from a shape that could not express an age
    // band, an expired order or the dependent's own restriction.
    // Note what does NOT change: `state.patient`. The account holder stays the
    // account holder for the whole session. The dependent lives beside them,
    // never in their place, so no ordinary screen can be handed somebody else's
    // record by a stale switch — the worst a mis-routed screen can do is show
    // the guardian their own data.
    case "SWITCH_ACTIVE_PROFILE": {
      return {
        ...state,
        viewingDependent: true,
        activeDependent: action.payload,
      };
    }

    case "EXIT_DEPENDENT_MODE": {
      return {
        ...state,
        viewingDependent: false,
        activeDependent: null,
      };
    }

    default:
      return state;
  }
}

// The signed-in account holder. This is ALWAYS `state.patient`, including
// while a dependent is on screen — see `DependentRoute`. A screen that wants
// "whose data am I showing" is asking the wrong question: the ordinary screens
// only ever show their own, and a dependent's data arrives through a
// dependent-scoped endpoint that names them in the path.
export const isViewingDependent = (state) => !!state?.viewingDependent;

/** The dependent currently on screen, as the SERVER described them. */
export const getActiveDependent = (state) => state?.activeDependent || null;

export function init(state) {
  if (!state)
    return {
      patient: null,
      doctorsIndexMap: {},
      notifications: [],
      firstLoading: true,
    };

  let doctorsIndexMap = {};
  (state.doctors || []).forEach((d, i) => {
    doctorsIndexMap[d.did] = i;
  });

  let newState = {
    ...state,
    notifications: (state.notifications || []).reverse(),
    doctorsIndexMap,
    guardianOf: state.guardianOf || [],
    viewingDependent: !!state.viewingDependent,
    activeDependent: state.activeDependent || null,
  };

  return newState;
}
